import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Maximize2, Minimize2, ListTodo, Calendar, Repeat } from 'lucide-react';
import StickyNote from './components/StickyNote';
import LandingPage from './components/LandingPage';
import NavBar from './components/NavBar';
import { COLLECTIONS, getCollectionForTaskType } from './hooks/useFirebase';
import { signInWithPopup, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, googleProvider } from './services/firebase';
import { quadrantInfo, quadrantOrder } from './components/quadrantInfo';
import { noteCategories } from './components/noteCategories';
import AIHourlySuggestion from './components/AIHourlySuggestion';
import TaskCreatorOverlay from 'components/TaskCreatorOverlay';
import { RecurringTaskResetService } from './services/recurringTaskResetService';
import { useFirebaseWithRateLimit } from './hooks/useFirebaseWithRateLimit';
import RateLimitErrorBanner from './components/RateLimitErrorBanner';
import { useScheduledTasks } from './hooks/useScheduledTasks';

const buttonStyle = "bg-purple-200 text-white p-2 rounded-full hover:bg-purple-500 transition-colors duration-200"

const QUADRANT_LABEL_TO_KEY = {
  'Do First': 'doFirst',
  'Decide': 'decide',
  'Delegate': 'delegate',
  'Delete': 'delete'
};

const App = () => {
  // Updated useFirebase hook with new collections
  const {
    notes,
    noteOrder,
    quadrantColors,
    userPreferences,
    updateUserPreferences,
    updateNoteOrder,
    loading,
    error,
    user,

    // Task management
    scheduledTasks,
    recurringTasks,
    addTask,
    updateTask,
    archiveTask,
    moveTask,
    rateLimitError
  } = useFirebaseWithRateLimit();

  // Existing state
  const [activeFeature, setActiveFeature] = useState(null);
  const [fullScreenQuadrant, setFullScreenQuadrant] = useState(null);
  const [quadrantRefs, setQuadrantRefs] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('Do First');
  const [lastClickedNote, setLastClickedNote] = useState(null);
  const [showTaskCreator, setShowTaskCreator] = useState(false);
  const [selectedQuadrant, setSelectedQuadrant] = useState(null);
  const [taskType, setTaskType] = useState('active'); // 'active', 'scheduled', 'recurring'
  const [showRateLimitError, setShowRateLimitError] = useState(false);
  const scheduledTaskService = useScheduledTasks(scheduledTasks, moveTask, user);

  useEffect(() => {
    if (rateLimitError) {
      setShowRateLimitError(true);
    }
  }, [rateLimitError]);

  useEffect(() => {
    const refs = {};
    quadrantOrder.forEach(key => {
      refs[key] = React.createRef();
    });
    setQuadrantRefs(refs);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleAddNote = (quadrantKey) => {
    setSelectedQuadrant(quadrantKey);
    setShowTaskCreator(true);
  };

  const createNote = async (noteData, taskType) => {
    const now = new Date();
    const baseTask = {
      relativeX: 0.4,
      relativeY: 0.4,
      subtasks: noteData.subtasks, // Use subtasks directly
      color: noteData.color,
      quadrant: noteData.quadrant,
      category: noteData.category,
      title: noteData.title,
      createdAt: now.toISOString(),
      isExpanded: true,
    };

    try {
      const collection = getCollectionForTaskType(taskType);

      switch (taskType) {
        case 'scheduled':
          await addTask({
            ...baseTask,
            scheduledFor: noteData.scheduledFor,
            dueAt: noteData.dueAt,
            status: 'scheduled'
          }, collection);
          break;

        case 'recurring': {
          const recurringTask = {
            ...baseTask,
            completedDates: [],
            assignedDays: noteData.assignedDays,
            lastCompletedAt: null,
            metrics: {
              totalCompletions: 0,
              currentStreak: 0,
              longestStreak: 0
            }
          };
          await addTask(recurringTask, collection);
          break;
        }

        case 'active':
        default:
          await addTask({
            ...baseTask,
            dueAt: noteData.dueAt
          }, collection);
          break;
      }

      setTaskType(taskType);
      // Switch quadrant view based on device/mode
      if (isMobile) {
        // Set the appropriate tab for the quadrant
        console.log(noteData.quadrant, quadrantInfo[noteData.quadrant].label);
        setActiveTab(quadrantInfo[noteData.quadrant].label);
      } else if (fullScreenQuadrant) {
        // If in fullscreen mode, switch to the new note's quadrant
        setFullScreenQuadrant(noteData.quadrant);
      }

    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const resetService = useMemo(() => {
    if (!user) return null;
    return new RecurringTaskResetService(
      (id, updates, collection) => updateTask(id, updates, collection),
      user.uid
    );
  }, [user, updateTask]);

  useEffect(() => {
    if (!user && resetService) {
      resetService.clearData();
    }
  }, [user, resetService]);


  const handleTaskTypeChange = (type) => {
    setTaskType(type);
    if (showTaskCreator) {
      setShowTaskCreator(false);  // Close and reopen with new type
      setTimeout(() => setShowTaskCreator(true), 0);
    }
  };

  // Add this ref to store the interval
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!user || !resetService || recurringTasks.length === 0) return;

    // Initial check for resets
    resetService.checkAndResetTasks(recurringTasks);

    // Calculate milliseconds until the start of the next hour
    const getMillisToNextHour = () => {
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
      return nextHour - now;
    };

    // Set up the first interval to start of next hour
    const initialTimeout = setTimeout(() => {
      // Perform check at the start of the hour
      resetService.checkAndResetTasks(recurringTasks);

      // Set up recurring checks at the start of each hour
      const hourlyInterval = setInterval(() => {
        resetService.checkAndResetTasks(recurringTasks);
      }, 3600000); // Exactly one hour

      // Store interval for cleanup
      intervalRef.current = hourlyInterval;
    }, getMillisToNextHour());

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetService.checkAndResetTasks(recurringTasks);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, recurringTasks, resetService]);

  const handleTaskAction = async (id, action, collection = COLLECTIONS.ACTIVE, updates = null) => {
    try {
      switch (action) {
        case 'update':
          if (updates) {
            await updateTask(id, updates, collection);
          }
          break;
        case 'archive':
          await archiveTask(id, collection);
          break;
        case 'delete':
          await archiveTask(id, collection);
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
    } catch (error) {
      console.error('Error performing task action:', error);
    }
  };

  const renderQuadrant = (key) => (
    <div
      key={key}
      ref={quadrantRefs[key]}
      className={`relative rounded ${quadrantColors[quadrantOrder.indexOf(key)] || quadrantInfo[key].defaultColor} quadrant flex flex-col items-center justify-center ${isMobile ? 'h-full' : fullScreenQuadrant === key ? 'col-span-2 row-span-2' : ''}`}
    >
      {/* Quadrant header */}
      <div className="absolute top-2 left-2 right-2 text-center">
        <div className="font-bold text-black">{quadrantInfo[key].label}</div>
        <div className="text-sm text-black">{quadrantInfo[key].sublabel}</div>
      </div>

      <div className="absolute top-2 left-2 flex space-x-2">
        {[
          { type: 'active', icon: ListTodo, label: 'Active Tasks' },
          { type: 'scheduled', icon: Calendar, label: 'Scheduled Tasks' },
          { type: 'recurring', icon: Repeat, label: 'Recurring Tasks' }
        ].map(({ type, icon: Icon, label }) => (
          <button
            key={type}
            className={`${buttonStyle} transition-all duration-200 ${taskType === type
              ? 'bg-purple-500 shadow-lg scale-110'
              : 'hover:bg-purple-300'
              }`}
            onClick={() => handleTaskTypeChange(type)}
            title={label}
          >
            <Icon size={16} />
          </button>
        ))}
      </div>

      {/* Restore quadrant icon */}
      <div className="text-black opacity-30 transform scale-150 pointer-events-none">
        {quadrantInfo[key].icon.large}
      </div>

      {/* Add task button */}
      <button
        className={`absolute bottom-2 right-2 ${buttonStyle}`}
        onClick={() => handleAddNote(key)}
      >
        <Plus size={16} />
      </button>

      {/* Fullscreen toggle - moved to top right */}
      {!isMobile && (
        <div className="absolute top-2 right-2">
          <button
            className={buttonStyle}
            onClick={() => toggleFullScreen(key)}
          >
            {fullScreenQuadrant === key ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      )}

      {sortedFilteredNotes
        .filter(note => note.quadrant === key)
        .map((note, index) => (
          <StickyNote
            key={note.id}
            {...note}
            isLastClicked={note.id === lastClickedNote}
            taskType={taskType}
            onTaskAction={handleTaskAction}
            onChangeColor={(id, updates) => updateTask(
              id,
              updates,
              getCollectionForTaskType(taskType)
            )}
            quadrant={note.quadrant}
            onChangeQuadrant={(id, newQuadrant) => updateTask(
              id,
              { quadrant: newQuadrant },
              getCollectionForTaskType(taskType)
            )}
            quadrantColors={quadrantColors}
            onMove={(id, newRelativeX, newRelativeY) => updateTask(
              id,
              { relativeX: newRelativeX, relativeY: newRelativeY },
              getCollectionForTaskType(taskType)
            )}
            onUpdateTimes={(id, updates) => {
              // Validate dueAt for scheduled tasks
              if (taskType === 'scheduled' &&
                updates.dueAt &&
                note.scheduledFor &&
                new Date(updates.dueAt) <= new Date(note.scheduledFor)) {
                console.error('Due date must be after scheduled date');
                return;
              }
              updateTask(id, updates, getCollectionForTaskType(taskType));
            }}
            onUpdateContent={(id, newSubtasks) => updateTask(
              id,
              { subtasks: newSubtasks }, // Update subtasks directly
              getCollectionForTaskType(taskType)
            )}
            onUpdateCategory={(id, newCategory) => updateTask(
              id,
              { category: newCategory },
              getCollectionForTaskType(taskType)
            )}
            onUpdateTitle={(id, newTitle) => updateTask(
              id,
              { title: newTitle },
              getCollectionForTaskType(taskType)
            )}
            // Existing props
            assignedDays={note.assignedDays}
            quadrantRef={quadrantRefs[key]}
            zIndex={index + 1}
            onNoteClick={(id) => handleNoteClick(id)}
            isExpanded={note.isExpanded}
            onToggleExpand={handleToggleExpand}
            hideCompleted={note.hideCompleted}
            onUpdateHideCompleted={(id, newHideCompleted) => updateTask(
              id,
              { hideCompleted: newHideCompleted },
              getCollectionForTaskType(taskType)
            )}
            // Additional props needed for TaskInfoModal
            scheduledFor={note.scheduledFor} // Add this prop
            metrics={note.metrics || {
              totalCompletions: 0,
              currentStreak: 0,
              longestStreak: 0,
              lastCompletedAt: null
            }}
          />
        ))}
    </div>
  );

  const handleNoteClick = (id) => {
    setLastClickedNote(id);
    const newOrder = noteOrder.filter(noteId => noteId !== id);
    newOrder.push(id);
    updateNoteOrder(newOrder);
  };

  const toggleFullScreen = (quadrantKey) => {
    setFullScreenQuadrant(fullScreenQuadrant === quadrantKey ? null : quadrantKey);
  };

  const handleSignIn = async (method, credentials) => {
    try {
      if (method === 'google') {
        const result = await signInWithPopup(auth, googleProvider);
        // Return the full result object
        return result;
      } else if (method === 'email') {
        // Implement email/password sign in here
        // ...
      }
    } catch (error) {
      console.error('Error signing in', error);
      throw error;
    }
  };

  const handleRegister = async (credentials) => {
    try {
      await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
    } catch (error) {
      console.error('Error registering new user', error);
    }
  };

  const handleForgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent to', email);
    } catch (error) {
      console.error('Error sending password reset email', error);
    }
  };

  const [showNextHourSuggestion, setShowNextHourSuggestion] = useState(false);

  const handleChangeFeature = (featureName) => {
    if (featureName === 'Next Hour') {
      setShowNextHourSuggestion(!showNextHourSuggestion);
    } else {
      setActiveFeature(activeFeature === featureName ? null : featureName);
    }
  };

  const [selectedCategories, setSelectedCategories] = useState(noteCategories.map(c => c.name));

  const handleToggleExpand = (id, isExpanded) => {
    updateTask(id, { isExpanded }, getCollectionForTaskType(taskType));
  };

  const handleFilterCategories = (categories) => {
    setSelectedCategories(categories);
  };

  const getTasksForType = (taskType) => {
    switch (taskType) {
      case 'scheduled':
        return scheduledTasks.map(task => ({
          ...task,
          scheduledFor: task.scheduledFor,
          dueAt: task.dueAt
        }));
      case 'recurring':
        return recurringTasks.map(task => ({
          ...task,
          assignedDays: task.assignedDays || [],
          metrics: task.metrics || {
            totalCompletions: 0,
            currentStreak: 0,
            longestStreak: 0,
            lastCompletedAt: null
          }
        }));
      case 'active':
      default:
        return notes.map(task => ({
          ...task,
          dueAt: task.dueAt
        }));
    }
  };

  const filteredNotes = selectedCategories.length === 0
    ? []
    : getTasksForType(taskType).filter(note => selectedCategories.includes(note.category));

  // Keep the existing sort logic
  const sortedFilteredNotes = [...filteredNotes].sort((a, b) => {
    return noteOrder.indexOf(a.id) - noteOrder.indexOf(b.id);
  });

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen">Error: {error.message}</div>;
  }

  const renderTabs = () => (
    <div className="flex overflow-x-auto bg-white shadow-md">
      {quadrantOrder.map((key) => {
        const info = quadrantInfo[key];
        return (
          <button
            key={key}
            className={`flex-1 py-2 px-4 ${activeTab === info.label ? 'bg-purple-200 font-bold' : 'bg-gray-100'} transition-colors duration-200 flex items-center justify-center`}
            onClick={() => setActiveTab(info.label)}
          >
            <div className="flex items-center space-x-2">
              <span>{info.icon.small}</span>
              <span className="hidden md:inline">{info.label}</span>
              <span>{info.locationIcon}</span>
            </div>
          </button>
        );
      })}
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen">Error: {error.message}</div>;
  }

  return (
    <div className="relative w-full h-screen flex flex-col"
      style={{
        backgroundImage: `linear-gradient(to right, ${userPreferences.gradientFrom}, ${userPreferences.gradientTo})`
      }}>
      <RateLimitErrorBanner
        error={showRateLimitError ? rateLimitError : null}
        onDismiss={() => setShowRateLimitError(false)}
      />
      {user ? (
        <>
          <NavBar
            activeFeature={activeFeature}
            onChangeFeature={handleChangeFeature}
            onFilterCategories={handleFilterCategories}
            currentFilters={selectedCategories}
            userPreferences={userPreferences}
            user={user}
            updateUserPreferences={updateUserPreferences}
            onCreateAINote={createNote}
            ref={(navbarRef) => {
              if (navbarRef) {
                window.openCategoryContext = navbarRef.openCategoryContext;
              }
            }}
          />

          {/* Main content */}
          <div className="flex-grow flex flex-col overflow-hidden">
            {isMobile && renderTabs()}
            {showNextHourSuggestion && (
              <AIHourlySuggestion
                notes={notes}
                userId={user.uid}
                userPreferences={userPreferences}
                onOpenCategoryContext={() => window.openCategoryContext?.()}
              />
            )}
            <div className={`flex-grow ${isMobile ? 'overflow-y-auto' : 'grid grid-cols-2 grid-rows-2'} gap-4 p-4 board`}>
              {isMobile ? (
                renderQuadrant(QUADRANT_LABEL_TO_KEY[activeTab] || quadrantOrder[0])
              ) : (
                fullScreenQuadrant ? (
                  renderQuadrant(fullScreenQuadrant)
                ) : (
                  quadrantOrder.map((key) => renderQuadrant(key))
                )
              )}
            </div>
          </div>

          {/* Task creator overlay */}
          {showTaskCreator && (
            <TaskCreatorOverlay
              isOpen={showTaskCreator}
              onClose={() => setShowTaskCreator(false)}
              onCreateNote={createNote}
              defaultNoteColor={userPreferences.defaultNoteColor}
              initialQuadrant={selectedQuadrant}
              taskType={taskType} // Pass current taskType as initial value
            />
          )}
        </>
      ) : (
        <LandingPage
          onSignIn={handleSignIn}
          onRegister={handleRegister}
          onForgotPassword={handleForgotPassword}
        />
      )}
    </div>
  );
};

export default App;